import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import { NotificationManager } from 'react-notifications';
import { showLoader, hideLoader } from '../../../store/common/slice';
import { isAdminSelector } from '../../../store/auth/selectors';
import { GAEvent } from '../../../common/google_analytics/google_analytics';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import { constants } from '../../../common/constants/constants';
import Input from '../../../common/simple/input';
import Checkbox from '../../../common/simple/Checkbox';
import User from '../../../images/newDesign/user.svg';
import Pen from '../../../images/managers/pen.png';
import Phone from '../../../images/newDesign/phone.svg';
import Mail from '../../../images/newDesign/mail.svg';
import { StyledDebtModal } from './debtList.styled';
import { SETTINGS } from '../../../common/settings';
import { parsePhoneToInternational } from '../../../common/utils/parseFunctions';

const DataModal = ({
    showDataModal,
    hideDataModal,
    firstModalContainer,
    changeModalContainer,
    tenantInfo,
    onInputChange,
    buildingID,
    setShowSuccessModal,
    currentModalData,
    buildingAddress,
    currency
}) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const client = useAccessToken();
    const isAdmin = useSelector(isAdminSelector);

    const [sendBtnDisabled, setSendBtnDisabled] = useState(false);
    const [edit, setEdit] = useState(false);
    const [checkboxes, setCheckboxes] = useState({
        sms: false,
        email: false,
        wa: false,
        owner: false,
        renter: false
    });
    const [disabledCheckboxes, setDidabledCheckboxes] = useState({
        sms: false,
        email: false,
        wa: false
    });
    const [disabledOwnershipCheckboxes, setDisabledOwnershipCheckboxes] = useState({
        renter: false,
        owner: false
    });

    const onHide = () => {
        hideDataModal();
        setCheckboxes({
            sms: false,
            email: false,
            wa: false,
            owner: false,
            renter: false
        });
    };

    useEffect(() => {
        const { sms, email, wa, owner, renter } = checkboxes;

        if (((sms || email) || wa) && (owner || renter)) setSendBtnDisabled(false);
        else setSendBtnDisabled(true);


        if (wa) setDidabledCheckboxes({...disabledCheckboxes, sms: true, email: true})
        else if (sms || email) setDidabledCheckboxes({...disabledCheckboxes, wa: true})
        else setDidabledCheckboxes({...disabledCheckboxes, sms: false, email: false, wa: false})

        if (wa && renter && owner) setCheckboxes({...checkboxes, owner: false, renter: false})
        else if (wa && renter) setDisabledOwnershipCheckboxes({...disabledCheckboxes, owner: true})
        else if (wa && owner) setDisabledOwnershipCheckboxes({...disabledCheckboxes, renter: true})
        else setDisabledOwnershipCheckboxes({renter: false, owner: false})
    }, [checkboxes]);

    const sendData = async () => {
        const { RenterEmail, OwnerName, OwnerPhone, RenterName, RenterPhone, OwnerEmail } = tenantInfo;

        const tenant_json = [{
            " renter email": RenterEmail ? RenterEmail : '',
            "apartment number": currentModalData.aptNumber,
            "owner name": OwnerName ? OwnerName : '',
            "owner phone": OwnerPhone ? OwnerPhone : '',
            "renter name": RenterName ? RenterName : '',
            "renter phone": RenterPhone ? RenterPhone : '',
            "owner email": OwnerEmail ? OwnerEmail : ''
        }];

        dispatch(showLoader());
        let payload = {
            tenant_json: JSON.stringify(tenant_json),
            building_id: buildingID ? buildingID : currentModalData.building
        };

        let result;
        if (JSON.stringify(currentModalData.tenantData) !== JSON.stringify(tenantInfo)) {
            result = await client.post(`/managers/tenants/create`, payload);
        }

        const paymentRequestResult = await client.post(`/managers/operations/request_payment/${buildingID ? buildingID : currentModalData.building}/${currentModalData.aptNumber}`, {
            'byEmail': checkboxes.email,
            'bySMS': checkboxes.sms,
            'toOwner': checkboxes.owner,
            'toRenter': checkboxes.renter
        });

        if (paymentRequestResult) {
            onHide();
            // See comments https://bllink.atlassian.net/browse/SSB-139
            // setShowSuccessModal(true);

            paymentRequestResult.forEach(result => {
                const { to, by } = result;
                if (result.status === 'done') {
                    if (result.reason !== 'TENANT_IS_OWNER') {
                        if (by === 'email') {
                            NotificationManager.success(t('reports.paymentRequestResults.fallBackSuccessEmail', { to }), '', 10000);
                        } else if (by === 'sms') {
                            NotificationManager.success(t('reports.paymentRequestResults.fallBackSuccessSMS', { to }), '', 10000);
                        }
                    }
                } else if (result.status === 'error') {
                   if (result.reason === 'NO_EMAIL') {
                       NotificationManager.error(t('reports.paymentRequestResults.errorNoEmail', { to }), '', 10000);
                   } else if (result.reason === 'NO_PHONE') {
                       NotificationManager.error(t('reports.paymentRequestResults.errorNoPhone', { to }), '', 10000);
                   } else {
                       if (by === 'email') {
                           NotificationManager.error(t('reports.paymentRequestResults.fallBackErrorEmail', { to }), '', 10000);
                       } else if (by === 'sms') {
                           NotificationManager.error(t('reports.paymentRequestResults.fallBackErrorSMS', { to }), '', 10000);
                       }
                   }
               }
            });

            if (isAdmin) {
                GAEvent(constants.GoogleAnalytics.Events.managerOperations, 'paymentRequestAdmin', 1, 'paymentRequestSuccess');
            } else{
                GAEvent(constants.GoogleAnalytics.Events.managerOperations, 'paymentRequestManager', 1, 'paymentRequestSuccess');
            }

        }

        if (result) setTimeout(() => window.location.reload(), 3000);

        dispatch(hideLoader());
    };

    const openWa = async () => {
        const { owner } = checkboxes;
        const { OwnerPhone, RenterPhone, OwnerName, RenterName } = tenantInfo;

        const phone = owner ? OwnerPhone : RenterPhone;
        const parsedPhone = parsePhoneToInternational(currency, phone);

        dispatch(showLoader());
        try {
            // todo should get from cache and not call every time
            const { building } = await client.managers.buildings.getManagerBuilding(
                buildingID, {skipStats: true, includeInternalID: true}
            );
            let managerName = '';
            if (building && building.partnerName){
                managerName = building.partnerName;
            }
    
            const response = await client.managers.operations.getShortLink(`${SETTINGS.server.baseUrl}/r/building/${buildingID}`);
    
            const text = t('reports.paymentRequestPopUp.waText', {
                personName: owner ? OwnerName : RenterName,
                buildingAddress: buildingAddress,
                apartmentNumber: currentModalData.aptNumber,
                paymentLink: response.link,
                buildingManagerName: managerName
            });
    
            if (parsedPhone) {
                GAEvent(constants.GoogleAnalytics.Events.managerOperations, 'sendViaPrivateWhatsApp', 1, 'sendViaPrivateWhatsAppLabel');
                window.open(`https://api.whatsapp.com/send?phone=${parsedPhone}&text=${encodeURIComponent(text)}&source=&data=`);
                onHide();
            } else NotificationManager.error(t('reports.phone_is_unavailable'))
        } catch (e) {
            return;
        }
        dispatch(hideLoader());


    };

    return (
        <StyledDebtModal
            centered
            show={showDataModal}
            onHide={onHide}
            size="lg"
        >
            <Modal.Header>
                <Modal.Title>{firstModalContainer ? t('reports.paymentRequestPopUp.paymentTitle') : t('reports.paymentRequestPopUp.allInfoTitle')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                {firstModalContainer && (
                    <>
                        <h5>{t('reports.paymentRequestPopUp.howToSendLabel')}?</h5>

                        <Checkbox
                            className={disabledCheckboxes.sms ? 'disabled' : ''}
                            label={t('reports.paymentRequestPopUp.labels.sms')}
                            checked={checkboxes.sms}
                            onChange={e => setCheckboxes({...checkboxes, sms: e})}
                            disabled={disabledCheckboxes.sms}
                        />

                        <Checkbox
                            className={disabledCheckboxes.email ? 'disabled' : ''}
                            label={t('reports.paymentRequestPopUp.labels.sendEmail')}
                            checked={checkboxes.email}
                            onChange={e => setCheckboxes({...checkboxes, email: e})}
                            disabled={disabledCheckboxes.email}
                        />

                        <Checkbox
                            className={disabledCheckboxes.wa ? 'disabled' : ''}
                            label={<OverlayTrigger
                                placement="top"
                                overlay={props => (
                                    <Tooltip {...props}>
                                        {t('reports.paymentRequestPopUp.sendWaNote')}
                                    </Tooltip>
                                )}
                            >

                                <div>{t('reports.paymentRequestPopUp.labels.sendWa')}</div>
                            </OverlayTrigger>
                            }
                            checked={checkboxes.wa}
                            onChange={e => setCheckboxes({...checkboxes, wa: e})}
                            disabled={disabledCheckboxes.wa}
                        />

                        <h5>{t('reports.paymentRequestPopUp.sendToLabel')}:</h5>

                        <Checkbox
                            className={disabledOwnershipCheckboxes.owner ? 'disabled' : ''}
                            label={t('reports.paymentRequestPopUp.labels.owner')}
                            checked={checkboxes.owner}
                            onChange={e => setCheckboxes({...checkboxes, owner: e})}
                            disabled={disabledOwnershipCheckboxes.owner}
                        />

                        <Checkbox
                            className={disabledOwnershipCheckboxes.renter ? 'disabled' : ''}
                            label={t('reports.paymentRequestPopUp.labels.renter')}
                            checked={checkboxes.renter}
                            onChange={e => setCheckboxes({...checkboxes, renter: e})}
                            disabled={disabledOwnershipCheckboxes.renter}
                        />

                        <button
                            className='bllink-button'
                            onClick={checkboxes.wa ? openWa : changeModalContainer}
                            disabled={sendBtnDisabled}
                        >
                            {t('reports.paymentRequestPopUp.btns.send')}
                        </button>
                    </>
                )}

                {!firstModalContainer && (
                    <>
                        <div className='columns'>
                            <div className='wrapper'>
                                <div className='header'>
                                    <span>{t('reports.paymentRequestPopUp.labels.name')}</span>
                                    <img src={User} alt='user' />
                                </div>
                                <div className='name-wrapper'>
                                    <span>{t('reports.paymentRequestPopUp.labels.owner')}</span>
                                    <span>{edit ? (
                                        <Input
                                            value={tenantInfo.OwnerName}
                                            onChange={onInputChange}
                                            name='OwnerName'
                                        />
                                    ) : <span>{tenantInfo.OwnerName}</span>}</span>
                                </div>
                                <div className='name-wrapper'>
                                    <span>{t('reports.paymentRequestPopUp.labels.renter')}</span>
                                    <span>{edit ? (
                                        <Input
                                            value={tenantInfo.RenterName}
                                            onChange={onInputChange}
                                            name='RenterName'
                                        />
                                    ) : <span>{tenantInfo.RenterName}</span>}</span>
                                </div>
                            </div>
                            <div className='wrapper'>
                                <div className='header'>
                                    <span>{t('reports.paymentRequestPopUp.labels.phone')}</span>
                                    <img src={Phone} alt='phone' />
                                </div>
                                {edit ? (
                                    <>
                                        <Input
                                            value={tenantInfo.OwnerPhone}
                                            onChange={onInputChange}
                                            name='OwnerPhone'
                                        />
                                        <Input
                                            value={tenantInfo.RenterPhone}
                                            onChange={onInputChange}
                                            name='RenterPhone'
                                        />
                                    </>
                                ) : (
                                    <>
                                        <div>{tenantInfo.OwnerPhone}</div>
                                        <div>{tenantInfo.RenterPhone}</div>
                                    </>
                                )}
                            </div>
                            <div className='wrapper'>
                                <div className='header'>
                                    <span>{t('reports.paymentRequestPopUp.labels.email')}</span>
                                    <img src={Mail} alt='mail' />
                                </div>
                                {edit ? (
                                    <>
                                        <Input
                                            value={tenantInfo.OwnerEmail}
                                            onChange={onInputChange}
                                            name='OwnerEmail'
                                        />
                                        <Input
                                            value={tenantInfo.RenterEmail}
                                            onChange={onInputChange}
                                            name='RenterEmail'
                                        />
                                    </>
                                ) : (
                                    <>
                                        <div>{tenantInfo.OwnerEmail}</div>
                                        <div>{tenantInfo.RenterEmail}</div>
                                    </>
                                )}
                            </div>
                            <div onClick={() => setEdit(!edit)} className='edit-icon'>
                                <img src={Pen} alt='pen' />
                            </div>

                        </div>
                        <button
                            className={'bllink-button'}
                            onClick={sendData}
                        >
                            {edit ? t('reports.paymentRequestPopUp.btns.saveSend') : t('reports.paymentRequestPopUp.btns.send')}
                        </button>
                    </>
                )}
            </Modal.Body>
        </StyledDebtModal>
    )
}

export default DataModal;
